/**
 * Wikilinks management page — PRD §5.11–5.14
 * Route: /wikilinks
 * Accessible from Signal Management page.
 */
import { api, ApiError } from "../lib/api";
import { renderHeader } from "../components/header";
import { navigate } from "../lib/router";
import { openPromoteModal } from "./threads";

interface WikilinkItem {
  id: string;
  name: string;
  signalCount: number;
  /** Null unless this wikilink has been promoted to a thread. */
  threadId: string | null;
}

function escHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

export function renderWikilinksPage(root: HTMLElement): void {
  root.appendChild(renderHeader());

  const shell = document.createElement("div");
  shell.className = "page-shell";
  shell.innerHTML = `
    <div class="wl-page">
      <div class="wl-page-header">
        <h2 class="serif">Manage Wikilinks</h2>
      </div>
      <div class="wl-filter-row">
        <input type="search" id="wl-search" placeholder="Filter by name…" />
      </div>
      <div id="wl-table-container"><p class="muted">Loading…</p></div>
    </div>`;
  root.appendChild(shell);

  let _allWikilinks: WikilinkItem[] = [];

  async function load(): Promise<void> {
    try {
      _allWikilinks = await api<WikilinkItem[]>("/wikilinks");
      render(_allWikilinks);
    } catch (_e) {
      shell.querySelector<HTMLDivElement>("#wl-table-container")!.innerHTML =
        `<p class="muted">Failed to load wikilinks.</p>`;
    }
  }

  function render(items: WikilinkItem[]): void {
    const container = shell.querySelector<HTMLDivElement>("#wl-table-container")!;
    if (!items.length) {
      container.innerHTML = `<p class="muted">No wikilinks yet.</p>`;
      return;
    }
    container.innerHTML = `
      <table class="wl-table">
        <thead>
          <tr>
            <th>Wikilink</th>
            <th>Signals</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          ${items.map((wl) => `
            <tr data-id="${wl.id}">
              <td><span class="wikilink-chip">#${escHtml(wl.name)}</span></td>
              <td class="mono">${wl.signalCount}</td>
              <td>
                <div class="wl-actions">
                  <button class="wl-btn-thread" data-id="${wl.id}" data-name="${escHtml(wl.name)}"
                          data-thread="${wl.threadId ?? ""}">${wl.threadId ? "Thread ›" : "Follow"}</button>
                  <button class="wl-btn-merge" data-id="${wl.id}" data-name="${escHtml(wl.name)}"
                          data-count="${wl.signalCount}">Merge</button>
                  <button class="wl-btn-rename" data-id="${wl.id}" data-name="${escHtml(wl.name)}"
                          data-count="${wl.signalCount}">Rename</button>
                  <button class="wl-btn-delete destructive-text" data-id="${wl.id}"
                          data-name="${escHtml(wl.name)}" data-count="${wl.signalCount}">Delete</button>
                </div>
              </td>
            </tr>`).join("")}
        </tbody>
      </table>`;

    container.querySelectorAll<HTMLButtonElement>(".wl-btn-thread").forEach((btn) => {
      btn.addEventListener("click", () => {
        const existing = btn.dataset.thread;
        if (existing) {
          navigate(`/threads/${encodeURIComponent(existing)}`);
          return;
        }
        openPromoteModal({ id: btn.dataset.id!, name: btn.dataset.name! }, (threadId) =>
          navigate(`/threads/${encodeURIComponent(threadId)}`),
        );
      });
    });
    container.querySelectorAll<HTMLButtonElement>(".wl-btn-merge").forEach((btn) => {
      btn.addEventListener("click", () =>
        openMergeModal(btn.dataset.id!, btn.dataset.name!, Number(btn.dataset.count), items, afterAction)
      );
    });
    container.querySelectorAll<HTMLButtonElement>(".wl-btn-rename").forEach((btn) => {
      btn.addEventListener("click", () =>
        openRenameModal(btn.dataset.id!, btn.dataset.name!, Number(btn.dataset.count), afterAction)
      );
    });
    container.querySelectorAll<HTMLButtonElement>(".wl-btn-delete").forEach((btn) => {
      btn.addEventListener("click", () =>
        openDeleteModal(btn.dataset.id!, btn.dataset.name!, Number(btn.dataset.count), afterAction)
      );
    });
  }

  async function afterAction(): Promise<void> {
    await load();
    // Re-apply search filter
    const q = shell.querySelector<HTMLInputElement>("#wl-search")!.value.trim().toLowerCase();
    if (q) {
      render(_allWikilinks.filter((wl) => wl.name.includes(q)));
    }
  }

  // Search/filter
  shell.querySelector<HTMLInputElement>("#wl-search")!.addEventListener("input", (e) => {
    const q = (e.target as HTMLInputElement).value.trim().toLowerCase();
    render(q ? _allWikilinks.filter((wl) => wl.name.includes(q)) : _allWikilinks);
  });

  load();
}

// ---------------------------------------------------------------------------
// Merge Wikilink Modal
// ---------------------------------------------------------------------------

function openMergeModal(
  sourceId: string,
  sourceName: string,
  sourceCount: number,
  allWikilinks: WikilinkItem[],
  onDone: () => void
): void {
  const others = allWikilinks.filter((wl) => wl.id !== sourceId);
  const sourceThread = allWikilinks.find((wl) => wl.id === sourceId)?.threadId ?? null;
  const overlay = document.createElement("div");
  overlay.className = "modal-overlay";
  overlay.innerHTML = `
    <div class="modal-box" style="max-width:440px">
      <div class="modal-header">
        <h3>Merge Wikilink</h3>
        <button class="icon-btn modal-close">✕</button>
      </div>
      <div class="modal-body">
        <p>Source: <strong>#${escHtml(sourceName)}</strong> (${sourceCount} signal${sourceCount !== 1 ? "s" : ""})</p>
        <div class="field-group">
          <label>Merge into</label>
          <select id="merge-target">
            <option value="">— Select target —</option>
            ${others.map((wl) => `<option value="${wl.id}">#${escHtml(wl.name)} (${wl.signalCount})</option>`).join("")}
          </select>
        </div>
        <p class="muted" id="merge-preview" style="font-size:13px;"></p>
        <p class="modal-err muted" id="merge-err"></p>
      </div>
      <div class="modal-footer">
        <button class="primary" id="merge-confirm">Merge</button>
        <button id="merge-cancel">Cancel</button>
      </div>
    </div>`;

  const close = (): void => { overlay.remove(); };
  overlay.querySelector(".modal-close")!.addEventListener("click", close);
  overlay.querySelector("#merge-cancel")!.addEventListener("click", close);
  overlay.addEventListener("mousedown", (e) => { if (e.target === overlay) close(); });

  const select = overlay.querySelector<HTMLSelectElement>("#merge-target")!;
  const preview = overlay.querySelector<HTMLParagraphElement>("#merge-preview")!;
  select.addEventListener("change", () => {
    const target = others.find((wl) => wl.id === select.value);
    if (!target) { preview.textContent = ""; return; }
    const lines = [`${sourceCount} signal(s) will be re-tagged as #${target.name}.`];
    // A merge deletes the source wikilink, so a thread on it has to go
    // somewhere. Saying which before the click, rather than after.
    if (sourceThread && target.threadId) {
      lines.push(
        `Both are threads: the one on #${sourceName} will be closed and folded into the one on #${target.name}.`,
      );
    } else if (sourceThread) {
      lines.push(`The thread on #${sourceName} will follow #${target.name}.`);
    }
    preview.textContent = lines.join(" ");
  });

  overlay.querySelector("#merge-confirm")!.addEventListener("click", async () => {
    const errEl = overlay.querySelector<HTMLParagraphElement>("#merge-err")!;
    if (!select.value) { errEl.textContent = "Please select a target wikilink."; return; }
    try {
      await api(`/wikilinks/${sourceId}/merge`, { method: "POST", body: { target_id: select.value } });
      close();
      onDone();
    } catch (err) {
      errEl.textContent = err instanceof ApiError ? err.message : "Merge failed.";
    }
  });

  document.body.appendChild(overlay);
}

// ---------------------------------------------------------------------------
// Rename Wikilink Modal
// ---------------------------------------------------------------------------

function openRenameModal(
  wikilinkId: string,
  currentName: string,
  signalCount: number,
  onDone: () => void
): void {
  const overlay = document.createElement("div");
  overlay.className = "modal-overlay";
  overlay.innerHTML = `
    <div class="modal-box" style="max-width:400px">
      <div class="modal-header">
        <h3>Rename Wikilink</h3>
        <button class="icon-btn modal-close">✕</button>
      </div>
      <div class="modal-body">
        <p>Current: <strong>#${escHtml(currentName)}</strong> (${signalCount} signal${signalCount !== 1 ? "s" : ""})</p>
        <div class="field-group">
          <label>New name <span class="muted">(lowercase, hyphenated)</span></label>
          <input type="text" id="rename-input" value="${escHtml(currentName)}" />
        </div>
        <p class="muted" style="font-size:13px;">${signalCount} signal(s) will be updated.</p>
        <p class="modal-err muted" id="rename-err"></p>
      </div>
      <div class="modal-footer">
        <button class="primary" id="rename-confirm">Rename</button>
        <button id="rename-cancel">Cancel</button>
      </div>
    </div>`;

  const close = (): void => { overlay.remove(); };
  overlay.querySelector(".modal-close")!.addEventListener("click", close);
  overlay.querySelector("#rename-cancel")!.addEventListener("click", close);
  overlay.addEventListener("mousedown", (e) => { if (e.target === overlay) close(); });

  overlay.querySelector("#rename-confirm")!.addEventListener("click", async () => {
    const errEl = overlay.querySelector<HTMLParagraphElement>("#rename-err")!;
    const newName = overlay.querySelector<HTMLInputElement>("#rename-input")!.value.trim().toLowerCase();
    if (!newName) { errEl.textContent = "Name cannot be empty."; return; }
    try {
      await api(`/wikilinks/${wikilinkId}/rename`, { method: "PATCH", body: { new_name: newName } });
      close();
      onDone();
    } catch (err) {
      errEl.textContent = err instanceof ApiError ? err.message : "Rename failed.";
    }
  });

  document.body.appendChild(overlay);
  overlay.querySelector<HTMLInputElement>("#rename-input")?.select();
}

// ---------------------------------------------------------------------------
// Delete Wikilink Modal
// ---------------------------------------------------------------------------

function openDeleteModal(
  wikilinkId: string,
  name: string,
  signalCount: number,
  onDone: () => void
): void {
  const overlay = document.createElement("div");
  overlay.className = "modal-overlay";
  overlay.innerHTML = `
    <div class="modal-box" style="max-width:420px">
      <div class="modal-header">
        <h3>Delete Wikilink</h3>
        <button class="icon-btn modal-close">✕</button>
      </div>
      <div class="modal-body">
        <p>Delete <strong>#${escHtml(name)}</strong>?</p>
        <p class="muted" style="font-size:13px;">This will remove the wikilink from ${signalCount} signal(s). This action cannot be undone.</p>
        <p class="modal-err muted" id="wldel-err"></p>
      </div>
      <div class="modal-footer">
        <button class="primary destructive" id="wldel-confirm">Delete</button>
        <button id="wldel-cancel">Cancel</button>
      </div>
    </div>`;

  const close = (): void => { overlay.remove(); };
  overlay.querySelector(".modal-close")!.addEventListener("click", close);
  overlay.querySelector("#wldel-cancel")!.addEventListener("click", close);
  overlay.addEventListener("mousedown", (e) => { if (e.target === overlay) close(); });

  overlay.querySelector("#wldel-confirm")!.addEventListener("click", async () => {
    const errEl = overlay.querySelector<HTMLParagraphElement>("#wldel-err")!;
    try {
      await api(`/wikilinks/${wikilinkId}`, { method: "DELETE" });
      close();
      onDone();
    } catch (err) {
      errEl.textContent = err instanceof ApiError ? err.message : "Delete failed.";
    }
  });

  document.body.appendChild(overlay);
}
